import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useRouter } from "next/router";
import { Form, Row, Col } from "react-bootstrap";

import {
  state_array as STATES,
  KEY_PRE_INC_ORDER_INFO,
  KEY_PRE_INC_ORDER_INFO_BACKUP,
  KEY_INC_SUMMARY,
  KEY_INC_SUMMARY_BACKUP,
  KEY_UPSELL_ORDER_UPDATE,
  getItemSessionStorage,
  setItemSessionStorage,
} from "../../../../variables/Variables";

import { hexToRGBA } from "../../../utils/theme";
import { FORM_FIELD_KEYS } from "../../../constants/forms";
import { getFieldProps } from "../../../utils/form-fields";

import TextInput from "../../controls/TextInput";
import MaskInput from "../../controls/MaskInput";
import Select from "../../controls/Select";
import Switch from "../../controls/Switch";
import StyledButton from "../../styled/StyledButton";

const StyledLLCIncForm = styled(Form)`
  padding-bottom: 62px;

  .inputs-block-title {
    margin-bottom: 32px;
    padding-left: 24px;
    padding-right: 24px;

    font-family: "MerriweatherLight";
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    text-transform: capitalize;
    color: #1b3858;
    text-shadow: 1px 1px 0px #ffffff;

    span {
      span {
        color: #c61f0c;
      }
    }
  }

  .input-title {
    margin-bottom: 4px;
    font-family: "LatoRegular";
    font-size: 14px;
    line-height: 24px;
    color: ${hexToRGBA("#1B3858", 0.7)};
    caret-color: ${hexToRGBA("#1B3858", 0.7)};

    > i {
      display: inline-block;
      right: unset;

      &:hover {
        color: inherit;
        cursor: auto;
      }
    }
  }

  .inputs-block-divider {
    margin-bottom: 46px;
  }

  .top-disclaimer {
    font-family: "MerriweatherLight";
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    color: #1b3858;

    span {
      display: block;
      margin-top: 16px;
      margin-bottom: 16px;
    }
  }

  .bottom-disclaimer {
    font-family: "LatoRegular";
    font-size: 10px;
    line-height: 22px;
    text-align: center;
    color: #1b3858;
    opacity: 0.7;
    text-shadow: 1px 1px 0px #ffffff;
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    padding-bottom: 42px;

    .input-title {
      position: relative;
      margin-bottom: 24px;

      > i {
        position: absolute;
        top: auto;
        bottom: -28px;
      }
    }
  }
`;

function LLCIncForm({ values = {}, onChange, onSubmit, preOrder }) {
  const router = useRouter();
  const [statesOptions, setStatesOptions] = useState([]);
  const isOrderUpdate = getItemSessionStorage(KEY_UPSELL_ORDER_UPDATE);

  const handleSubmitButtonMouseDown = useCallback(() => {
    onChange && onChange({ target: { id: "suspendedSubmit", value: true } });
  }, [onChange]);

  const handleCancel = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const cashedPreOrderBackup = getItemSessionStorage(KEY_PRE_INC_ORDER_INFO_BACKUP);
      const cashedIncSummaryBackup = getItemSessionStorage(KEY_INC_SUMMARY_BACKUP);

      if (cashedPreOrderBackup) {
        setItemSessionStorage(KEY_PRE_INC_ORDER_INFO, cashedPreOrderBackup);
      }

      if (cashedIncSummaryBackup) {
        setItemSessionStorage(KEY_INC_SUMMARY, cashedIncSummaryBackup);
      }

      router.push("/LLCIncFinalize");
    },
    [router]
  );

  useEffect(() => {
    fetch(process.env.NEXT_PUBLIC_REACT_APP_API + "/category/EIN_INC/states", {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.error) throw res.error;
        setStatesOptions(res.map(({ code, name }) => ({ title: name, value: code })));
      })
      .catch((e) => {
        let salePage = window.location.href;
        let salePageURLArr = salePage.split("/");
        let salePageURL = (salePageURLArr[0] + "//" + salePageURLArr[2]).replace(/\/+$/, "");

        fetch(salePageURL + "/crm/category/EIN_INC/states", {
          method: "GET",
          headers: { Accept: "application/json" },
        })
          .then((res) => res.json())
          .then((res) => {
            if (res.error) throw res.error;
            setStatesOptions(res.map(({ code, name }) => ({ title: name, value: code })));
          })
          .catch((e) => {
            setStatesOptions([]);
            console.log(e);
          });
      });
  }, []);

  return (
    <StyledLLCIncForm id="IncForm" noValidate validated={values.validated} onSubmit={onSubmit}>
      <Row>
        <Col xs={12}>
          <div className="top-disclaimer">
            <span>
              Please confirm the details below so that we can successfully complete your request to incorporate your LLC
              in &nbsp;{STATES[values.state]}
            </span>
            <span>
              Incorporating your business is an essential step towards successful operations. A Limited Liability
              Company protects your business, and most importantly yourself, from any lawsuits or issues that may arise
              in the course of doing business. If you do not have an LLC, you could be personally liable for damages,
              whether you made a mistake or not.
            </span>
            <span>
              Please take a moment to confirm or correct any details below, then press the ‘Submit Order’ button to
              complete your submission.
            </span>
          </div>
        </Col>
        <Col xs={12}>
          <div className="inputs-block-divider" />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Legal information</span>
          </div>
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.tradeName)}
            value={values[FORM_FIELD_KEYS.tradeName]}
            matchValue={values[FORM_FIELD_KEYS.state]}
            onChange={onChange}
            disabled={preOrder?.order?.productCodes.includes("EIN_LLC")}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.altCompanyName)}
            value={values[FORM_FIELD_KEYS.altCompanyName]}
            matchValue={values[FORM_FIELD_KEYS.tradeName]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12}>
          <Select
            {...getFieldProps(FORM_FIELD_KEYS.state)}
            label="Would you like to incorporate in a different state?"
            value={values[FORM_FIELD_KEYS.state]}
            options={statesOptions}
            validated={values.validated}
            onChange={onChange}
          />
        </Col>
        <Col xs={12}>
          <div className="inputs-block-divider" />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Primary Officer</span>
          </div>
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.principalOfficerFirstName)}
            value={values[FORM_FIELD_KEYS.principalOfficerFirstName]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.principalOfficerMiddleName)}
            value={values[FORM_FIELD_KEYS.principalOfficerMiddleName]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.principalOfficerLastName)}
            value={values[FORM_FIELD_KEYS.principalOfficerLastName]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12} md={6}>
          <MaskInput
            {...getFieldProps(FORM_FIELD_KEYS.customerPhone)}
            value={values[FORM_FIELD_KEYS.customerPhone]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12} md={6}>
          <TextInput
            {...getFieldProps(FORM_FIELD_KEYS.customerEmail)}
            loading={values.verifying}
            value={values[FORM_FIELD_KEYS.customerEmail]}
            onChange={onChange}
          />
        </Col>
        <Col xs={12}>
          <div className="inputs-block-divider" />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Corporate Address (All Legal Correspondence will be received here)</span>
          </div>
        </Col>

        {values.raProduct && (
          <>
            <Col xs={12}>
              <div className="input-title">
                <strong>
                  Notice: This information will be public record. If you do not wish for your information to be public,
                  we highly recommend the use of a Resident Agent. A resident agent will keep your information private,
                  and receive legal mail on your behalf. Do you wish to use our Resident Agent service?
                </strong>
                <br />
                <i>*Billed ${values.raPrice}/Annually</i>
              </div>
            </Col>
            <Col xs={12}>
              <div className="inputs-block-divider" />
            </Col>
            <Col xs={12}>
              <Switch
                {...getFieldProps(FORM_FIELD_KEYS.residentAgent)}
                value={values[FORM_FIELD_KEYS.residentAgent]}
                onChange={onChange}
              />
            </Col>
            <Col xs={12}>
              <div className="inputs-block-divider" />
            </Col>
          </>
        )}

        {(values.residentAgent === "false" || !values.raProduct) && (
          <>
            <Col xs={12} md={6}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.corpAddressAddress1)}
                value={values[FORM_FIELD_KEYS.corpAddressAddress1]}
                onChange={onChange}
              />
            </Col>
            <Col xs={12} md={6}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.corpAddressAddress2)}
                value={values[FORM_FIELD_KEYS.corpAddressAddress2]}
                onChange={onChange}
              />
            </Col>
            <Col xs={12} md={6}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.corpAddressCity)}
                value={values[FORM_FIELD_KEYS.corpAddressCity]}
                onChange={onChange}
              />
            </Col>
            <Col xs={12} md={6}>
              <TextInput
                {...getFieldProps(FORM_FIELD_KEYS.corpAddressZip)}
                value={values[FORM_FIELD_KEYS.corpAddressZip]}
                onChange={onChange}
              />
            </Col>
            <Col xs={12}>
              <Select
                {...getFieldProps(FORM_FIELD_KEYS.corpAddressState)}
                value={values[FORM_FIELD_KEYS.corpAddressState]}
                validated={values.validated}
                onChange={onChange}
              />
            </Col>
            <Col xs={12}>
              <div className="inputs-block-divider" />
            </Col>
          </>
        )}
      </Row>
      <Row>
        <Col xs={12}>
          <div className="inputs-block-title">
            <span>Order Summary</span>
          </div>
        </Col>
        {values.state !== "ME" && (
          <Col xs={12}>
            <div className="input-title">
              <span>
                State Incorporation Fee (Charged by {STATES[values.state]}) - <b>${values.stateFee}.00</b>
              </span>
            </div>
          </Col>
        )}
        <Col xs={12}>
          <div className="input-title">
            <span>
              LLC Processing Fee - <b>${values.price}.00</b>
            </span>
          </div>
        </Col>
        {values.residentAgent === "true" && values.raProduct && (
          <Col xs={12}>
            <div className="input-title">
              <span>
                Resident Agent (Annually) - <b>${values.raPrice}.00</b>
              </span>
            </div>
          </Col>
        )}
        <Col xs={12}>
          <div className="inputs-block-divider" />
        </Col>
      </Row>
      <Row>
        <Col xs={0} md={3} />
        <Col xs={12} md={6}>
          <StyledButton type="submit" id="LLCIncSubmitButton" onMouseDown={handleSubmitButtonMouseDown}>
            {isOrderUpdate ? "Update Order" : "Submit Order"}
          </StyledButton>
        </Col>
        <Col xs={0} md={3} />
      </Row>
      {isOrderUpdate && (
        <Row>
          <Col xs={0} md={3} />
          <Col xs={12} md={6}>
            <StyledButton secondary onClick={handleCancel}>
              Cancel
            </StyledButton>
          </Col>
          <Col xs={0} md={3} />
        </Row>
      )}
      <Row>
        <Col xs={12}>
          <div className="bottom-disclaimer">
            <span>
              By pressing Submit Order above, you authorize us to process your request to form an official Limited
              Liability Liability Company in AL. Once your order is submitted, your credit card will be charged for the
              State Incorporation Fee and Our Processing Fee immediately. After submission, our trained experts will
              review your submission and check that your requested LLC Name is available. If not available, the
              Alternate Name will be used. If we encounter any issues with your submission, you will be contacted by
              e-mail and/or phone. If your order is successfully processed, we will not be able to issue a refund under
              any circumstances, as the fees will have been submitted to the State. However, if we are unable to
              complete your order, a full refund will be made.
            </span>
          </div>
        </Col>
      </Row>
    </StyledLLCIncForm>
  );
}

export default LLCIncForm;
